import { Button } from '@nerdwallet/currency';
import Phone from '@nerdwallet/react-icon/gizmo/Phone';
import classNames from 'classnames';
import get from 'lodash/get';
import Image from 'next/image';
import { useMemo, useRef } from 'react';

import { useSearchParams } from 'next/navigation';
import SegmentImpression from '~/app/components/segment-impression';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { useIdentity } from '~/app/lib/redux/selectors/identity-selector';
import { getShoppingFinishedId } from '~/app/lib/get-shopping-finish-id';
import { useIsHydrated } from '~/app/lib/use-is-hydrated';
import { buttonUrlWithCustomQueryParams } from '../../dynamic-grid-helpers';
import { SegmentTrackingProps } from '../../segment/segment-tracking-form';
import {
  addCtaApplyNowLinkTracking,
  useDynamicAnalytics,
} from '../../segment/use-dynamic-analytics';
import type { DynamicTextComponentProps } from '../dynamic-text/dynamic-text-component';
import { BUTTON_TYPE_OPTIONS, BUTTON_URL_SOURCE_TYPES } from './constants';
import { DynamicApplyNowButton } from './dynamic-apply-now-button';
import { useCustomButtonColors } from './use-custom-button-colors';
import {
  CustomButtonColorsType,
  CustomButtonIconType,
} from './use-dynamic-button-form';
import { getButtonType, getCustomIconPosition } from './utils';

import { useModal } from '../../dynamic-grid-modal';
import styles from './dynamic-button.module.scss';

type DynamicButtonProps = {
  buttonType?: string;
  customButtonColors?: CustomButtonColorsType;
  customButtonIcon?: CustomButtonIconType;
  showPhoneIcon?: boolean;
  text?: string;
  url?: string;
  authenticatedUrl?: string;
  urlSource?: string;
  marketplaceOfferId?: string;
  ctaLinkOption?: string;
  captureCustomQueryParams?: boolean;
  ctaSubtext: DynamicTextComponentProps;
  ctaExtraText: DynamicTextComponentProps;
  blockButton?: boolean;
  targetBlank?: boolean;
  segment?: SegmentTrackingProps;
};

const PhoneButton = ({
  showPhoneIcon,
  text,
  url,
}: {
  showPhoneIcon: boolean;
  text: string;
  url: string;
}) => (
  <span
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
  >
    {showPhoneIcon && <Phone size={16} style={{ marginRight: '8px' }} />}
    {text || url || 'Button'}
  </span>
);

const mockMarketplaceEntity = {
  cta: {
    showPhoneIcon: true,
    type: 'APPLY_NOW',
    text: 'Apply now',
    subtext: 'Loading subtext',
  },
};

export const DynamicButton = ({
  buttonType = BUTTON_TYPE_OPTIONS[0].value, // default to primary
  customButtonColors,
  customButtonIcon,
  showPhoneIcon = false,
  text = '',
  url = '#',
  authenticatedUrl = '',
  urlSource = BUTTON_URL_SOURCE_TYPES.CUSTOM_URL,
  marketplaceOfferId,
  ctaLinkOption = '',
  captureCustomQueryParams = false,
  ctaSubtext,
  ctaExtraText,
  blockButton = false,
  targetBlank = false,
  segment,
}: DynamicButtonProps) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const { isSignedIn } = useIdentity();
  const buttonTypeVariant = getButtonType(buttonType);
  const isPhoneNumber = urlSource === BUTTON_URL_SOURCE_TYPES.PHONE_NUMBER;
  const isMarketplaceOffer =
    urlSource === BUTTON_URL_SOURCE_TYPES.MARKETPLACE_OFFER;
  const isApplyNowButtonType = buttonType === BUTTON_TYPE_OPTIONS[4].value;
  const { marketplaceOffers } = useDynamicGrid();
  const searchParams = useSearchParams();

  const isHydrated = useIsHydrated();

  const isModal = urlSource === BUTTON_URL_SOURCE_TYPES.MODAL;
  const { setHash } = useModal({ modalId: url });

  const selectedMarketplaceOffer = useMemo(() => {
    return marketplaceOffers?.find(
      (offer: any) => offer?.id === marketplaceOfferId
    );
  }, [marketplaceOffers, marketplaceOfferId]);

  let buttonUrl = url;
  if (authenticatedUrl && isSignedIn) {
    buttonUrl = authenticatedUrl;
  }
  const offerUrlLink = get(selectedMarketplaceOffer, ctaLinkOption, '');
  if (isMarketplaceOffer && offerUrlLink) {
    buttonUrl = offerUrlLink;
  }

  if (captureCustomQueryParams) {
    buttonUrl = buttonUrlWithCustomQueryParams(
      buttonUrl,
      searchParams
    ).toString();
  }

  let href;
  if (isPhoneNumber) href = `tel:${url?.replace(/\D/g, '')}`;
  if (!isModal && !isPhoneNumber) href = buttonUrl || '#';

  const iconPosition = getCustomIconPosition(
    customButtonIcon?.iconPosition ?? ''
  );
  const customButtonColorValues = buttonType === BUTTON_TYPE_OPTIONS[3].value;

  useCustomButtonColors({
    buttonRef,
    customButtonColorValues,
    customButtonColors,
  });

  const customButtonStyle = {
    backgroundColor:
      customButtonColors?.backgroundColor === 'HEXCOLOR'
        ? customButtonColors?.backgroundHexColor
        : customButtonColors?.backgroundColor,
    border: customButtonColors?.borderWidth
      ? `${customButtonColors?.borderWidth}px solid ${customButtonColors?.borderColor}`
      : undefined,
  };

  const {
    impressionViewProps,
    marketplaceButtonProps,
    handleTrackInteraction,
  } = useDynamicAnalytics({
    entityName: segment?.entityName || '',
    impressionSection: segment?.impressionSection || '',
    impressionPosition: segment?.impressionPosition || '',
    selectedMarketplaceOffer,
    isApplyNowButtonType,
    location: segment?.location || '',
    subLocation: segment?.subLocation || '',
    enableInteractionTracking:
      segment?.enableInteractionTracking || isMarketplaceOffer,
    modalId: isModal ? url : undefined,
  });

  let onClick;
  if (isModal)
    onClick = () => {
      setHash(url);
      handleTrackInteraction();
    };

  if (segment?.enableInteractionTracking) {
    onClick = () => {
      handleTrackInteraction();
    };
  }

  marketplaceButtonProps.shopping_finish_id = segment?.includeShoppingFinishId
    ? getShoppingFinishedId()
    : undefined;

  // Apply Now button
  if (isApplyNowButtonType) {
    const marketplaceEntity = selectedMarketplaceOffer || mockMarketplaceEntity;
    const updatedMarketPlaceEntity = isHydrated
      ? addCtaApplyNowLinkTracking(marketplaceEntity, marketplaceButtonProps)
      : marketplaceEntity;

    const isBankingType = marketplaceEntity?.productType === 'banking';
    const subText =
      isBankingType &&
      marketplaceEntity?.cta?.subtext &&
      marketplaceEntity?.federallyInsuredAnnotation
        ? `${marketplaceEntity?.cta?.subtext}, ${marketplaceEntity?.federallyInsuredAnnotation}`
        : ctaSubtext?.text || marketplaceEntity?.cta?.subtext;

    const marketplaceCta = {
      ...updatedMarketPlaceEntity.cta,
      text: text || marketplaceEntity?.cta?.text,
      subtext: subText,
    };

    if (captureCustomQueryParams) {
      const ctaLinkWithCustomQueryParams = buttonUrlWithCustomQueryParams(
        marketplaceCta.link,
        searchParams
      ).toString();
      marketplaceCta.link = ctaLinkWithCustomQueryParams;
    }

    if (marketplaceCta?.subtext?.includes('or call')) {
      marketplaceCta.phoneNumber = marketplaceEntity.phoneNumber;
    }

    return (
      <SegmentImpression
        eventName="ApplyNowButton Viewed"
        eventProps={marketplaceButtonProps}
        className={classNames({
          [styles.segmentImpressionWrapper]: blockButton,
        })}
      >
        <DynamicApplyNowButton
          cta={marketplaceCta}
          ctaSubtext={ctaSubtext}
          ctaExtraText={ctaExtraText}
          onClick={handleTrackInteraction}
        />
      </SegmentImpression>
    );
  }

  let target = targetBlank ? '_blank' : undefined;
  if (isMarketplaceOffer) target = '_blank';
  if (isModal) target = undefined;

  const eventProps = {
    ...impressionViewProps,
  };

  if (segment?.enableInteractionTracking) {
    eventProps.location = segment?.location || '';
    eventProps.sub_location = segment?.subLocation || '';
    eventProps.impression_section = segment?.impressionSection || '';
    eventProps.impression_position = segment?.impressionPosition || '';
  }

  return (
    <SegmentImpression
      eventName="CtaButton Viewed"
      eventProps={eventProps}
      className={classNames({
        [styles.segmentImpressionWrapper]: blockButton,
      })}
      disabled={!segment?.enableInteractionTracking}
    >
      <div
        ref={buttonRef}
        className={classNames({
          [styles.button]: customButtonColorValues,
        })}
        style={
          {
            color:
              customButtonColors?.textColor === 'HEXCOLOR'
                ? customButtonColors?.textHexColor
                : customButtonColors?.textColor || '#ffffff',
            '--button-text-color':
              customButtonColors?.textColor === 'HEXCOLOR'
                ? customButtonColors?.textHexColor
                : customButtonColors?.textColor || '#ffffff',
            width: blockButton ? '100%' : 'auto',
          } as React.CSSProperties
        }
      >
        <Button
          block={blockButton}
          aria-label={isPhoneNumber ? 'Call us' : undefined}
          {...buttonTypeVariant}
          href={href}
          target={target}
          style={customButtonStyle}
          onClick={onClick}
        >
          {isPhoneNumber && (
            <PhoneButton
              showPhoneIcon={showPhoneIcon}
              text={text}
              url={buttonUrl}
            />
          )}

          {!isPhoneNumber && (
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: iconPosition.direction as
                  | 'row'
                  | 'row-reverse'
                  | 'column'
                  | 'column-reverse',
              }}
            >
              {customButtonIcon?.iconUrl && (
                <Image
                  style={{ ...iconPosition.margin }}
                  objectFit="contain"
                  height={customButtonIcon?.iconHeight || 16}
                  width={customButtonIcon?.iconWidth || 16}
                  src={customButtonIcon?.iconUrl}
                  alt="Button Icon"
                />
              )}
              {text || 'Button'}
            </span>
          )}
        </Button>
      </div>
    </SegmentImpression>
  );
};
